/**
 * FranchiseDashboardPage — `/franchise` index landing.
 *
 * Live wiring:
 *   - GET /v1/franchise/dashboard/central/map      → KPI tiles + by-state breakdown
 *   - GET /v1/franchise/dashboard/central/revenue  → revenue + top franchisees
 *   - GET /v1/franchise/dashboard/alerts           → anomaly feed
 *   - GET /v1/franchise/franchises                 → recent outlets list
 *
 * Per-state rollups live here; per-outlet geo-pins (from
 * `GET /v1/franchise/ops/coordinates/pins`) render on the dedicated
 * `/franchise/national-map` page — we link out to it from the by-state panel.
 */
import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, FileText, MapPin, Store, Wallet } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import {
  getAlerts,
  getCentralMap,
  getCentralRevenue,
  listFranchises,
} from '../../lib/api/franchise';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';

const TODAY = (): string => new Date().toISOString().slice(0, 10);
const THIRTY_DAYS_AGO = (): string => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
};

function isDenied(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 401 || err.status === 403);
}

export function FranchiseDashboardPage(): JSX.Element {
  const map = useQuery({
    queryKey: ['franchise', 'dashboard', 'map'],
    queryFn: getCentralMap,
    retry: false,
    staleTime: 30_000,
  });
  const fromDate = THIRTY_DAYS_AGO();
  const toDate = TODAY();
  const revenue = useQuery({
    queryKey: ['franchise', 'dashboard', 'revenue', fromDate, toDate],
    queryFn: () => getCentralRevenue({ fromDate, toDate }),
    retry: false,
    staleTime: 30_000,
  });
  const alerts = useQuery({
    queryKey: ['franchise', 'dashboard', 'alerts'],
    queryFn: getAlerts,
    retry: false,
    staleTime: 30_000,
  });
  const outlets = useQuery({
    queryKey: ['franchise', 'dashboard', 'outlets'],
    queryFn: () => listFranchises({ pageSize: 10 }),
    retry: false,
    staleTime: 30_000,
  });

  const anyDenied =
    isDenied(map.error) ||
    isDenied(revenue.error) ||
    isDenied(alerts.error) ||
    isDenied(outlets.error);

  const tier1 = map.data?.byTier.find((b) => b.tier === 'TIER_1')?.count ?? 0;
  const tier2 = map.data?.byTier.find((b) => b.tier === 'TIER_2')?.count ?? 0;
  const tier3 = map.data?.byTier.find((b) => b.tier === 'TIER_3')?.count ?? 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Franchise network"
        description="Operator console for NIPOST franchise outlets across Nigeria."
        actions={
          <div className="flex items-center gap-2">
            <Link
              to="/franchise/hierarchy"
              className="px-3 py-2 border border-border rounded-lg text-sm font-medium hover:bg-gray-50"
            >
              Hierarchy
            </Link>
            <Link
              to="/franchise/applications"
              className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark inline-flex items-center gap-2"
            >
              Applications <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        }
      />

      {anyDenied ? (
        <DeferredNotice title="Permission required">
          Your account does not have permission to view the franchise dashboard. Please contact your administrator.
        </DeferredNotice>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <KPICard
          title="Total live"
          value={map.isPending ? '—' : String(map.data?.totalLive ?? 0)}
          hint="Active outlets across all states"
          icon={Building2}
        />
        <KPICard title="Tier 1 — Full PO" value={String(tier1)} icon={Building2} />
        <KPICard title="Tier 2 — Postal Agent" value={String(tier2)} icon={Store} />
        <KPICard title="Tier 3 — Last-Mile" value={String(tier3)} icon={MapPin} />
        <KPICard
          title="30-day revenue"
          value={revenue.isPending ? '—' : `₦${revenue.data?.totalRevenue ?? '0'}`}
          hint={`${fromDate} → ${toDate}`}
          icon={Wallet}
        />
        <KPICard
          title="Alerts"
          value={alerts.isPending ? '—' : String(alerts.data?.alerts.length ?? 0)}
          hint="open anomalies"
          icon={FileText}
          tone="warning"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Outlets by state</h2>
          {map.isPending ? (
            <p className="text-sm text-muted">Loading…</p>
          ) : map.isError ? (
            <DeferredNotice title="Map unavailable">{(map.error as Error).message}</DeferredNotice>
          ) : (map.data?.byState.length ?? 0) === 0 ? (
            <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
              No outlets registered yet.
            </p>
          ) : (
            <div className="bg-white border border-border rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                  <tr>
                    <th className="text-left px-4 py-2">State</th>
                    <th className="text-right px-4 py-2">Outlets</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {map.data?.byState.map((row) => (
                    <tr key={row.state} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-xs text-gray-700">{row.state}</td>
                      <td className="px-4 py-2 text-right text-xs font-mono">{row.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-[11px] text-muted mt-2">
            Need geo-pins?{' '}
            <Link to="/franchise/national-map" className="text-nipost-green hover:underline">
              Open the national map
            </Link>
            {' '}for individual outlet coordinates.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Top franchisees · 30 days</h2>
          {revenue.isPending ? (
            <p className="text-sm text-muted">Loading…</p>
          ) : revenue.isError ? (
            <DeferredNotice title="Revenue unavailable">{(revenue.error as Error).message}</DeferredNotice>
          ) : (revenue.data?.topFranchises.length ?? 0) === 0 ? (
            <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
              No revenue snapshots in the last 30 days.
            </p>
          ) : (
            <div className="bg-white border border-border rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                  <tr>
                    <th className="text-left px-4 py-2">Franchise</th>
                    <th className="text-right px-4 py-2">Revenue</th>
                    <th className="text-right px-4 py-2">Commission</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {revenue.data?.topFranchises.map((row) => (
                    <tr key={row.franchiseId} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-mono text-xs">
                        <Link
                          to={`/franchise/franchises/${encodeURIComponent(row.franchiseId)}`}
                          className="text-nipost-green hover:underline"
                        >
                          {row.franchiseId.slice(0, 8)}…
                        </Link>
                      </td>
                      <td className="px-4 py-2 text-right text-xs font-mono">₦{row.revenue}</td>
                      <td className="px-4 py-2 text-right text-xs font-mono">₦{row.commission}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Recent outlets</h2>
        {outlets.isPending ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : outlets.isError ? (
          <DeferredNotice title="Service unreachable">{(outlets.error as Error).message}</DeferredNotice>
        ) : (outlets.data?.items.length ?? 0) === 0 ? (
          <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
            No franchise outlets yet.
          </p>
        ) : (
          <div className="bg-white border border-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                <tr>
                  <th className="text-left px-4 py-2">Number</th>
                  <th className="text-left px-4 py-2">Name</th>
                  <th className="text-left px-4 py-2">Tier</th>
                  <th className="text-left px-4 py-2">State</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-right px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {outlets.data?.items.map((f) => (
                  <tr key={f.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono text-xs">{f.franchiseNumber}</td>
                    <td className="px-4 py-2 text-xs text-gray-700">{f.legalName}</td>
                    <td className="px-4 py-2 text-xs text-gray-700">{f.tierCode}</td>
                    <td className="px-4 py-2 text-xs text-gray-700">{f.state}</td>
                    <td className="px-4 py-2"><StatusBadge status={f.status} /></td>
                    <td className="px-4 py-2 text-right">
                      <Link
                        to={`/franchise/franchises/${encodeURIComponent(f.id)}`}
                        className="text-xs font-semibold text-nipost-green hover:underline"
                      >
                        Open →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
